"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// The Kaal Sarp check embedded in the /kaal-sarp-dosha/ article (the #2460
// pattern: the server section imports only this tiny client module, and the
// form, place search, engine client and result panel load as an async chunk).
// It loads the offer-free KaalSarpDoshCheck, never KaalSarpDoshClient: the
// article's existing report bridge stays its single offer.
//
// Activation is element-scoped: a reader scrolling the article never downloads
// the island; a tap, focus or submit on the form shell does. The shell carries
// the island's own form props (h3 under the section's H2, a required birth
// time), so every frame before the island mounts reads the same words.
// translate="no" covers the result panel, which re-renders on "Check another
// chart" (tests/translate-safe-islands.test.ts).
//
// The engine replies well after the submit tap, outside CLS's input window, and
// its result or failure alert used to resize the check under the reader. The
// wrapper now reserves the tallest post-submit state synchronously in the
// submit event (the #2777 /nakshatra/ approach), inside the input window, and
// keeps it for the mount: an error or "Check another chart" keeps the space,
// because a shrink would be a counted shift too. Natural heights measured
// 2026-10-22 (next dev + Playwright, the engine mocked; 320/360/390/414/480/
// 640/768/1024/1280 wide): the form is 607 px on phones and 525 from 640; the
// tallest state is the form with the engine-failure alert, 675 up to 414, 635
// at 480 and 593 from 640; the detected result is 647 at 320 and 533 from 480,
// the clear result 261 or less. Hence 720 on phones and 640 from sm, about 45
// px of headroom each.
const SUBMIT_RESERVE = "min-h-[720px] sm:min-h-[640px]";

const SHELL_PROPS = {
  submitLabel: "Check Kaal Sarp Dosh",
  heading: "Your birth details",
  headingLevel: "h3",
  requireBirthTime: true,
  showTrustStrip: true,
} as const;

const KaalSarpCheckIsland = dynamic(() => import("@/app/kaal-sarp-dosh/KaalSarpDoshCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

export default function KaalSarpChartCheck() {
  const { ready, activate } = useDeferredToolActivation({ scope: "element" });
  const [reserved, setReserved] = useState(false);

  return (
    <div
      translate="no"
      className={reserved ? SUBMIT_RESERVE : undefined}
      // Capture, so the reserve lands in the submit event itself, before the
      // form's own handler awaits the engine.
      onSubmitCapture={() => setReserved(true)}
    >
      {ready ? (
        <KaalSarpCheckIsland formHeadingLevel="h3" resultHeadingLevel="h3" requireBirthTime />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={activate} />
      )}
    </div>
  );
}
